import { useContent } from "@/cms/ContentContext";
import { useLang } from "@/i18n/LangContext";

/**
 * Returns a resolver `tx(path, fallback)` that:
 *  1) reads `content.texts.<path>_<lang>` from the CMS if set (and non-empty)
 *  2) otherwise returns the provided fallback (from i18n copy)
 *
 * Example: tx("hero.overline", t.hero.overline)
 * Example: tx("servicos.items.0.title", t.servicos.items[0].title)
 */
export const useTx = () => {
  const { content } = useContent();
  const { lang } = useLang();
  const texts = content?.texts || {};

  return (path, fallback) => {
    const parts = path.split(".");
    let node = texts;
    for (const p of parts) {
      if (node == null) return fallback;
      node = node[p];
    }
    if (node == null) return fallback;
    const val = typeof node === "object" ? node[`_${lang}`] || node[lang] : null;
    // Also support the direct `<path>_<lang>` scheme (leaf-level).
    if (val) return val;
    return fallback;
  };
};

/**
 * Simpler leaf-level helper for the *_pt / *_en scheme.
 * Example: leaf("hero.overline") reads content.texts.hero.overline_pt / _en
 */
export const useLeafTx = () => {
  const { content } = useContent();
  const { lang } = useLang();

  return (path, fallback) => {
    const parts = path.split(".");
    const leaf = parts.pop();
    let node = content?.texts || {};
    for (const p of parts) {
      if (node == null) return fallback;
      node = node[p];
    }
    if (node == null) return fallback;
    const val = node[`${leaf}_${lang}`];
    return val && String(val).trim() ? val : fallback;
  };
};
